/**
 * Audit trail — shared, admin-only change history for every object type.
 *
 * Model: each mutation records an immutable AuditEntry against an object
 * (type + id). Entries start empty and accumulate as changes are actually made
 * this session (no fabricated history). A store instruments its own mutations
 * by calling `logUpsert` / `logDelete` / `logAction`; the object's ⋯ menu opens
 * the shared AuditTrailDrawer, gated to System Administrators.
 *
 * Persistence: localStorage (per browser session), so a trail survives reloads
 * while the app has no real backend. At wiring time the real audit-events op
 * replaces this store with no UI change.
 */
import { useEffect, useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import * as apiClient from "@v0y4ge/api-client";
import { getRealUser, getImpersonation, getActiveTenantSlug, onTenantChange } from "../shell/access";

export type AuditObjectType =
  | "event" | "hub" | "template" | "landing" | "message" | "flow"
  | "ticketType" | "ticketGroup" | "service" | "serviceCategory"
  | "question" | "questionGroup" | "survey" | "promo" | "guest"
  | "location" | "staff" | "staffGroup" | "resource" | "resourceGroup"
  | "product"
  | "approvalProcess" | "branding" | "settings";

export const AUDIT_TYPE_LABEL: Record<AuditObjectType, string> = { // ui-config — enum display labels, not data
  event: "Event", hub: "Event hub", template: "Event template", landing: "Landing page",
  message: "Message", flow: "Messaging flow", ticketType: "Ticket type", ticketGroup: "Ticket group",
  service: "Service", serviceCategory: "Service category", question: "Booking question",
  questionGroup: "Question group", survey: "Survey", promo: "Coupon / promo", guest: "Customer",
  location: "Location", staff: "Staff member", staffGroup: "Staff group", resource: "Resource",
  resourceGroup: "Resource group", approvalProcess: "Approval process", branding: "Branding", settings: "Settings",
  product: "Product",
};

export type AuditSource = "web" | "api" | "event-day" | "system" | "public";
export interface AuditFieldChange { field: string; from: unknown; to: unknown }
export interface AuditActor {
  name: string; init: string; color: string; email?: string;
  /** Set when the entry was made by an admin ACTING AS someone else. `name` is
   *  then the admin — the person actually responsible — and this is who they
   *  were acting as. Never collapse the two: "who did it" and "whose account it
   *  looked like" are different questions an audit trail has to answer. */
  onBehalfOf?: { name: string; email?: string };
}
export interface AuditEntry {
  id: string;
  type: AuditObjectType;
  objectId: string;
  objectName: string;
  action: string;            // created | updated | published | unpublished | cancelled | archived | deleted | duplicated | restored | …
  version: number;           // v1 = created; increments per recorded change
  at: number;                // epoch ms
  actor: AuditActor;
  source: AuditSource;
  changes: AuditFieldChange[];
}

// Cross-tenant leak fix (docket 2026-07-26): this was ONE global localStorage
// key for every tenant's audit trail — a real leak of who-changed-what across
// tenant boundaries. Now keyed per active tenant; the in-memory list swaps on
// tenant change (never carries tenant A's entries into tenant B's session).
const lsKey = () => `jrni.audit.v1:${getActiveTenantSlug() ?? "none"}`;
let _entries: AuditEntry[] = [];
try { const raw = localStorage.getItem(lsKey()); if (raw) _entries = JSON.parse(raw); } catch { /* noop */ }
const _subs = new Set<() => void>();
const _persist = () => { try { localStorage.setItem(lsKey(), JSON.stringify(_entries)); } catch { /* noop */ } };
const _emit = () => { _persist(); _subs.forEach((f) => { try { f(); } catch { /* noop */ } }); };
onTenantChange(() => {
  try { const raw = localStorage.getItem(lsKey()); _entries = raw ? JSON.parse(raw) : []; } catch { _entries = []; }
  _subs.forEach((f) => { try { f(); } catch { /* noop */ } });
});

let _seq = 0;
const _uid = () => `au_${Date.now().toString(36)}${(_seq++).toString(36)}`;

function _actor(): AuditActor {
  /* The ADMIN, not the person they are acting as. This read used to be
     `getUser()`, which during impersonation returns the TARGET — so every
     change an admin made while acting as someone else was recorded against
     that someone else, and the trail said the staff member did it. */
  const u = getRealUser();
  const imp = getImpersonation();
  const actor: AuditActor = { name: u.name || "You", init: u.init || "?", color: u.color || T.p1, email: u.email };
  if (imp) actor.onBehalfOf = { name: imp.target.name || imp.target.email || "—", email: imp.target.email };
  return actor;
}

/** Fields never worth surfacing in a diff (internal wizard/session flags). */
const IGNORE_KEYS = new Set(["updated", "updatedAt", "updated_at", "lastEdit", "_creation", "_editingId", "_draftId", "_published", "_savedDraft", "_templateMode", "_templateId", "_templateMeta", "sold", "rev"]);
const _norm = (v: unknown) => { try { return JSON.stringify(v ?? null); } catch { return String(v); } };

/** Shallow diff of two records → the fields that changed (internal keys skipped). */
export function diffRecords(prev: Record<string, unknown> | null | undefined, next: Record<string, unknown>): AuditFieldChange[] {
  const out: AuditFieldChange[] = [];
  const keys = new Set([...Object.keys(prev || {}), ...Object.keys(next || {})]);
  for (const k of keys) {
    if (k.startsWith("_") || IGNORE_KEYS.has(k)) continue;
    const a = prev ? prev[k] : undefined;
    const b = next[k];
    if (typeof a === "function" || typeof b === "function") continue;
    if (_norm(a) !== _norm(b)) out.push({ field: k, from: a, to: b });
  }
  return out;
}

/* ── REQ-025 wiring: the real server-authored trail ─────────────────────
 * When `listAuditEntries` exists (drop29), the SERVER is the source of truth:
 * every studio mutation route writes its own entry (fail-open), so the client
 * stops writing (log* become no-ops) and reads via the op. Without the op
 * (older preview stub), the localStorage store below keeps working unchanged. */
type ListAuditEntriesFn = (p: { objectType: AuditObjectType; objectId: string }) => Promise<{ entries?: Array<Record<string, unknown>> }>;
function _listOp(): ListAuditEntriesFn | null {
  const f = (apiClient as Record<string, unknown>).listAuditEntries
    ?? ((apiClient as Record<string, unknown>).auditEntries as Record<string, unknown> | undefined)?.listAuditEntries;
  return typeof f === "function" ? (f as ListAuditEntriesFn) : null;
}
function _fromDto(d: Record<string, unknown>): AuditEntry {
  const at = typeof d.at === "number" ? d.at : Date.parse(String(d.at ?? "")) || Date.now();
  const actor = (d.actor ?? {}) as Record<string, unknown>;
  return {
    id: String(d.id ?? ""), type: d.type as AuditObjectType, objectId: String(d.objectId ?? ""),
    objectName: String(d.objectName ?? ""), action: String(d.action ?? "updated"),
    version: Number(d.version) || 1, at,
    actor: {
      name: String(actor.name ?? "—"), init: String(actor.init ?? "?"), color: String(actor.color ?? T.p1),
      email: actor.email == null ? undefined : String(actor.email),
      ...(actor.onBehalfOf ? { onBehalfOf: {
        name: String((actor.onBehalfOf as Record<string, unknown>).name ?? "—"),
        email: (actor.onBehalfOf as Record<string, unknown>).email == null ? undefined : String((actor.onBehalfOf as Record<string, unknown>).email),
      } } : {}),
    },
    source: (d.source as AuditSource) ?? "web",
    changes: Array.isArray(d.changes) ? (d.changes as AuditFieldChange[]) : [],
  };
}

export function record(entry: Omit<AuditEntry, "id" | "version" | "at" | "actor" | "source"> & Partial<Pick<AuditEntry, "actor" | "source" | "at">>): AuditEntry {
  const version = _entries.filter((e) => e.type === entry.type && e.objectId === entry.objectId).length + 1;
  const rec: AuditEntry = {
    id: _uid(),
    type: entry.type, objectId: entry.objectId, objectName: entry.objectName,
    action: entry.action, version, at: entry.at ?? Date.now(),
    actor: entry.actor ?? _actor(), source: entry.source ?? "web",
    changes: entry.changes ?? [],
  };
  // Server-authored mode: the mutation route already recorded this change —
  // don't double-write a client copy (reads come from listAuditEntries).
  if (_listOp()) return rec;
  _entries.push(rec);
  _emit();
  return rec;
}

/** Log a create/update by diffing the previous record against the next one.
 * No entry is recorded for an update that changed nothing. */
export function logUpsert(type: AuditObjectType, prev: Record<string, unknown> | null | undefined, next: Record<string, unknown>, opts?: { name?: string; id?: string; action?: string; source?: AuditSource }): void {
  const objectId = String(opts?.id ?? next.id ?? "");
  if (!objectId) return;
  const objectName = String(opts?.name ?? next.name ?? next.title ?? next.label ?? objectId);
  if (!prev) {
    record({ type, objectId, objectName, action: opts?.action ?? "created", changes: [], source: opts?.source });
    return;
  }
  const changes = diffRecords(prev, next);
  if (!changes.length) return;
  // Derive a status-aware action when status changed.
  let action = opts?.action ?? "updated";
  const st = changes.find((c) => c.field === "status");
  if (st && !opts?.action) {
    const to = String(st.to || "").toLowerCase();
    if (["live", "upcoming", "scheduled", "published", "active"].includes(to)) action = "published";
    else if (["cancelled", "canceled"].includes(to)) action = "cancelled";
    else if (["past", "archived", "completed", "inactive"].includes(to)) action = "archived";
    else if (["draft"].includes(to)) action = "unpublished";
  }
  record({ type, objectId, objectName, action, changes, source: opts?.source });
}

export function logDelete(type: AuditObjectType, obj: Record<string, unknown>, opts?: { name?: string; id?: string; source?: AuditSource }): void {
  const objectId = String(opts?.id ?? obj.id ?? "");
  if (!objectId) return;
  const objectName = String(opts?.name ?? obj.name ?? obj.title ?? obj.label ?? objectId);
  record({ type, objectId, objectName, action: "deleted", changes: [], source: opts?.source });
}

export function logAction(type: AuditObjectType, objectId: string, objectName: string, action: string, opts?: { changes?: AuditFieldChange[]; source?: AuditSource }): void {
  if (!objectId) return;
  record({ type, objectId, objectName, action, changes: opts?.changes ?? [], source: opts?.source });
}

export function entriesFor(type: AuditObjectType, objectId: string): AuditEntry[] {
  return _entries.filter((e) => e.type === type && e.objectId === String(objectId)).sort((a, b) => b.at - a.at);
}

export function useObjectAudit(type: AuditObjectType, objectId: string): AuditEntry[] {
  const [, force] = useState(0);
  const [server, setServer] = useState<AuditEntry[] | null>(null);
  useEffect(() => { const cb = () => force((n) => n + 1); _subs.add(cb); return () => { _subs.delete(cb); }; }, []);
  useEffect(() => {
    let live = true;
    const op = _listOp();
    if (!op) { setServer(null); return; }
    op({ objectType: type, objectId: String(objectId) })
      .then((r) => { if (live) setServer((r?.entries ?? []).map(_fromDto).sort((a, b) => b.at - a.at)); })
      .catch((e) => { console.warn("listAuditEntries failed:", e); if (live) setServer([]); });
    return () => { live = false; };
  }, [type, objectId]);
  return server ?? entriesFor(type, objectId);
}

/* ── Formatting ─────────────────────────────────────────────────────────── */
export function humanizeField(k: string): string {
  return k.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
    .replace(/^./, (c) => c.toUpperCase());
}
export function formatValue(v: unknown): string {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? `${v.length} item${v.length === 1 ? "" : "s"}` : "none";
  if (typeof v === "object") return "updated";
  const s = String(v);
  return s.length > 80 ? s.slice(0, 77) + "…" : s;
}
export function formatWhen(at: number): string {
  const d = new Date(at);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}
const SOURCE_LABEL: Record<AuditSource, string> = { web: "Web", api: "API", "event-day": "Event Day", system: "System", public: "Public page" };
export const sourceLabel = (s: AuditSource) => SOURCE_LABEL[s] || s;

/* ── Export ─────────────────────────────────────────────────────────────── */
export function exportAuditCsv(objectName: string, entries: AuditEntry[]): void {
  const head = ["When", "Version", "Actor", "Email", "Acting as", "Action", "Source", "Field", "From", "To"];
  const esc = (v: unknown) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const lines = [head.join(",")];
  entries.forEach((e) => {
    const who = e.actor.onBehalfOf?.name ?? "";
    if (e.changes.length) {
      e.changes.forEach((c) => lines.push([formatWhen(e.at), "v" + e.version, e.actor.name, e.actor.email || "", who, e.action, sourceLabel(e.source), humanizeField(c.field), formatValue(c.from), formatValue(c.to)].map(esc).join(",")));
    } else {
      lines.push([formatWhen(e.at), "v" + e.version, e.actor.name, e.actor.email || "", who, e.action, sourceLabel(e.source), "", "", ""].map(esc).join(","));
    }
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `audit-${objectName.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export function exportAuditPdf(title: string, entries: AuditEntry[]): void {
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const rows = entries.map((e) => {
    const changeHtml = e.changes.length
      ? `<ul style="margin:4px 0 0;padding-left:16px">${e.changes.map((c) => `<li><b>${esc(humanizeField(c.field))}</b>: ${esc(formatValue(c.from))} → ${esc(formatValue(c.to))}</li>`).join("")}</ul>`
      : "";
    return `<tr>
      <td>${esc(formatWhen(e.at))}</td>
      <td>v${e.version}</td>
      <td>${esc(e.actor.name)}${e.actor.onBehalfOf ? `<div style="color:#6b6580;font-size:11px">acting as ${esc(e.actor.onBehalfOf.name)}</div>` : ""}</td>
      <td><b>${esc(e.action)}</b>${changeHtml}</td>
      <td>${esc(sourceLabel(e.source))}</td>
    </tr>`;
  }).join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Audit trail — ${esc(title)}</title>
    <style>
      body{font:13px/1.5 -apple-system,'DM Sans',system-ui,sans-serif;color:#1a1523;padding:32px;}
      h1{font-size:20px;margin:0 0 2px} .sub{color:#6b6580;font-size:12px;margin-bottom:18px}
      table{width:100%;border-collapse:collapse} th,td{text-align:left;vertical-align:top;padding:8px 10px;border-bottom:1px solid #e6e4ee;font-size:12px}
      th{text-transform:uppercase;letter-spacing:.04em;font-size:10px;color:#6b6580}
      ul{color:#3a3550;font-size:11.5px}
    </style></head><body>
    <h1>Audit trail — ${esc(title)}</h1>
    <div class="sub">${entries.length} ${entries.length === 1 ? "entry" : "entries"} · generated ${esc(formatWhen(Date.now()))}</div>
    <table><thead><tr><th>When</th><th>Version</th><th>Actor</th><th>Action &amp; changes</th><th>Source</th></tr></thead>
    <tbody>${rows || '<tr><td colspan="5">No changes recorded yet.</td></tr>'}</tbody></table>
    <script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html); w.document.close();
}
